import { motion } from 'framer-motion';
import { usePlayerStore } from '@/store/player-store';
import ProfileSelector from '@/components/ProfileSelector';
import { Card, CardContent } from '@/components/ui/card';
import { Sparkles, Gamepad2, Trophy, User, Settings } from 'lucide-react';

interface HomePageProps {
  onNavigate: (page: 'lobby' | 'leaderboard' | 'profile' | 'game') => void;
}

export default function HomePage({ onNavigate }: HomePageProps) {
  const { currentProfile } = usePlayerStore();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* 顶部导航 */}
      <nav className="p-4 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-red-600 rounded-lg flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <span className="text-xl font-bold text-white">Texas Hold'em</span>
        </div>
        
        {currentProfile && (
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-white font-medium">{currentProfile.name}</p>
              <p className="text-emerald-400 text-sm">{currentProfile.chips.toLocaleString()} 筹码</p>
            </div>
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center">
              <User className="w-5 h-5 text-white" />
            </div>
          </div>
        )}
      </nav>

      {/* 主内容 */}
      <main className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-2 gap-8 items-start">
          {/* 左侧 - 欢迎和快速操作 */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                德州扑克
                <span className="block text-amber-400">大师赛</span>
              </h1>
              <p className="text-slate-400 text-lg">
                纯前端扑克游戏，无需后端，数据本地存储。
                与 AI 对战，提升你的扑克技巧！
              </p>
            </div>

            {currentProfile ? (
              <div className="grid grid-cols-2 gap-4">
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Card 
                    className="bg-gradient-to-br from-amber-600 to-amber-700 border-amber-500 cursor-pointer hover:shadow-lg hover:shadow-amber-500/20 transition-all"
                    onClick={() => onNavigate('lobby')}
                  >
                    <CardContent className="p-6">
                      <Gamepad2 className="w-10 h-10 text-white mb-3" />
                      <h3 className="text-xl font-bold text-white">开始游戏</h3>
                      <p className="text-amber-100 text-sm mt-1">进入游戏大厅</p>
                    </CardContent>
                  </Card>
                </motion.div>

                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Card 
                    className="bg-slate-800/50 border-slate-700 cursor-pointer hover:bg-slate-800 transition-all"
                    onClick={() => onNavigate('leaderboard')}
                  >
                    <CardContent className="p-6">
                      <Trophy className="w-10 h-10 text-amber-400 mb-3" />
                      <h3 className="text-xl font-bold text-white">排行榜</h3>
                      <p className="text-slate-400 text-sm mt-1">查看本周排名</p>
                    </CardContent>
                  </Card>
                </motion.div>

                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Card 
                    className="bg-slate-800/50 border-slate-700 cursor-pointer hover:bg-slate-800 transition-all"
                    onClick={() => onNavigate('profile')}
                  >
                    <CardContent className="p-6">
                      <User className="w-10 h-10 text-blue-400 mb-3" />
                      <h3 className="text-xl font-bold text-white">个人中心</h3>
                      <p className="text-slate-400 text-sm mt-1">查看统计数据</p>
                    </CardContent>
                  </Card>
                </motion.div>

                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Card 
                    className="bg-slate-800/50 border-slate-700 cursor-pointer hover:bg-slate-800 transition-all"
                  >
                    <CardContent className="p-6">
                      <Settings className="w-10 h-10 text-slate-400 mb-3" />
                      <h3 className="text-xl font-bold text-white">设置</h3>
                      <p className="text-slate-400 text-sm mt-1">游戏偏好设置</p>
                    </CardContent>
                  </Card>
                </motion.div>
              </div>
            ) : (
              <div className="p-6 bg-slate-800/50 rounded-xl border border-slate-700">
                <p className="text-slate-400 mb-4">请先创建或选择一个玩家档案开始游戏</p>
              </div>
            )}
          </motion.div>

          {/* 右侧 - 档案选择 */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
          >
            <ProfileSelector />
          </motion.div>
        </div>
      </main>

      {/* 底部信息 */}
      <footer className="container mx-auto px-4 py-6 mt-8 border-t border-slate-800">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-slate-500 text-sm">
          <p>纯前端游戏 · 数据存储在本地浏览器</p>
          <div className="flex gap-6">
            <span>休闲场: 免费</span>
            <span>锦标赛: 赢取积分</span>
            <span>AI 对战: 3种难度</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
